import { FilterAttribute, Filter } from "../components/layerControl/FilterModal";

interface LayerFeature {
  properties: Record<string, any>;
  [key: string]: any;
}

const inferType = (value: any): "string" | "number" | "date" | "enum" => {
  if (value === null || value === undefined) return "string";
  
  if (typeof value === "number") return "number";
  
  // Check if it's a date string
  if (typeof value === "string") {
    const dateRegex = /^\d{4}-\d{2}-\d{2}/;
    if (dateRegex.test(value)) return "date";
  }
  
  return "string";
};

const inferEnumValues = (features: LayerFeature[], key: string): string[] | undefined => {
  if (!features || features.length === 0) return undefined;
  
  const uniqueValues = new Set<string>();
  features.forEach(feature => {
    const value = feature.properties?.[key];
    if (value !== null && value !== undefined) {
      uniqueValues.add(String(value));
    }
  });
  
  // If there are 10 or fewer unique values, treat as enum
  if (uniqueValues.size > 0 && uniqueValues.size <= 10) {
    return Array.from(uniqueValues).sort();
  }
  
  return undefined;
};

export const getAttributesFromLayer = (source: any): FilterAttribute[] => {
  if (!source) return [];
  
  // Handle different source structures
  let features: LayerFeature[] | undefined;
  
  // Case 1: GeoJSON FeatureCollection
  if (source.type === 'FeatureCollection' && source.features) {
    features = source.features;
  }
  // Case 2: Direct features array
  else if (Array.isArray(source.features)) {
    features = source.features;
  }
  // Case 3: Source IS the features array
  else if (Array.isArray(source)) {
    features = source;
  }
  // Case 4: Source has a data property with features
  else if (source.data?.features) {
    features = source.data.features;
  }
  
  if (!features || features.length === 0) {
    return [];
  }
  
  const firstFeature = features[0];
  
  if (!firstFeature?.properties) {
    return [];
  }
  
  const attributes: FilterAttribute[] = [];
  
  Object.entries(firstFeature.properties).forEach(([key, value]) => {
    // Skip internal/system properties
    if (key.startsWith('_') || key === 'id') return;
    
    const type = inferType(value);
    const enumValues = type === "string" ? inferEnumValues(features!, key) : undefined;
    
    attributes.push({
      value: key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      type: enumValues ? "enum" : type,
      enumValues,
    });
  });
  
  return attributes;
};

const evaluateFilter = (feature: LayerFeature, filter: Filter): boolean => {
  const value = feature.properties?.[filter.attribute];
  
  if (value === null || value === undefined) return false;
  
  switch (filter.operator) {
    case "equals":
      return String(value).toLowerCase() === String(filter.value).toLowerCase();
    
    case "contains":
      return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
    
    case "greater":
      return Number(value) > Number(filter.value);
    
    case "less":
      return Number(value) < Number(filter.value);
    
    case "between":
      if (!filter.value2) return false;
      const numValue = Number(value);
      return numValue >= Number(filter.value) && numValue <= Number(filter.value2);
    
    case "after":
      return new Date(value) > new Date(filter.value);
    
    case "before":
      return new Date(value) < new Date(filter.value);
    
    default:
      return true;
  }
};

export const applyFilters = (source: any, filters: Filter[]): any => {
  if (!source || filters.length === 0) return source;
  
  // Handle different source structures
  let features: LayerFeature[] | undefined;
  let isGeoJSON = false;
  let isArray = false;
  
  if (source.type === 'FeatureCollection' && source.features) {
    features = source.features;
    isGeoJSON = true;
  } else if (Array.isArray(source.features)) {
    features = source.features;
  } else if (Array.isArray(source)) {
    features = source;
    isArray = true;
  } else if (source.data?.features) {
    features = source.data.features;
  }
  
  if (!features) return source;
  
  const filteredFeatures = features.filter(feature => {
    return filters.every(filter => evaluateFilter(feature, filter));
  });
  
  // Return in the same structure as input
  if (isArray) {
    return filteredFeatures;
  } else if (isGeoJSON) {
    return {
      ...source,
      features: filteredFeatures,
    };
  } else {
    return {
      ...source,
      features: filteredFeatures,
    };
  }
};
